import { useEffect, useMemo, useRef, useState } from 'react';
import api from '../../api/axios';
import { toAssetUrl } from '../../utils/assetBase';
import ImageLightbox from '../../components/ImageLightbox';
import WatermarkedImage from '../../components/WatermarkedImage';
import { SeasonBanner, SEASON_THEMES } from './SeasonalLayer';

const BRAND = 'var(--qr-primary, #0090bb)';

const formatMoney = (v) => `${Number(v || 0).toFixed(2)} ₼`;
const normalizeText = (v) => String(v || '').toLocaleLowerCase('az');

// QR porsiya — çəki/litr məhsulu "500 q — 15.00 ₼" kimi göstərilsin (admin qr_portion_grams təyin edibsə).
// Qiymət kg/lt qiymətindən avtomatik hesablanır; kassa yenə çəkiylə işləyir.
const portionOf = (item) => {
    const grams = Number(item?.qr_portion_grams || 0);
    if (grams > 0 && (item?.sale_type === 'weight' || item?.sale_type === 'liter')) {
        const unit = item.sale_type === 'liter' ? 'ml' : 'q';
        return { grams, unit, price: Number(item.price || 0) * grams / 1000, label: `${grams} ${unit}` };
    }
    return null;
};

// Variant qiyməti — item porsiyalı çəki/litr məhsuludursa, variantın kg qiymətini də porsiyaya çevir.
const variantDisplayPrice = (item, variant) => {
    const p = portionOf(item);
    return p ? Number(variant?.price || 0) * p.grams / 1000 : Number(variant?.price || 0);
};
const makeCartKey = (itemId, variantId = null) => (variantId ? `${itemId}_v${variantId}` : String(itemId));

// Sıralama üçün effektiv qiymət: variantlı məhsulda ən ucuz variant qiyməti
const effectivePrice = (item) => {
    const variants = item?.variants || [];
    if (variants.length > 0) return Math.min(...variants.map((v) => Number(v.price || 0)));
    return Number(item?.price || 0);
};
// Sıra backend-dən gəlir (superadmin "Məhsul sıralaması" seçimi) — burada yenidən sıralamırıq.
const sortByPrice = (items) => [...(items || [])];

const QrMenuV2 = ({ token, onHome, season = '' }) => {
    const [tenant, setTenant] = useState(null);
    const [occupancy, setOccupancy] = useState(null);
    const [categories, setCategories] = useState([]);
    // QR tema (superadmindən): --qr-primary = buttonlar/accent, --qr-bg = fon
    useEffect(() => {
        // Mövsümi tema (Halloween / Yeni il / Novruz) seçilibsə onun rəngləri (önizləmə ?season= daxil)
        const th = season && SEASON_THEMES[season] ? { ...(tenant?.qr_theme || {}), ...SEASON_THEMES[season] } : (tenant?.qr_theme || {});
        const root = document.documentElement;
        if (th.primary) root.style.setProperty('--qr-primary', th.primary); else root.style.removeProperty('--qr-primary');
        if (th.bg) { root.style.setProperty('--qr-bg', th.bg); document.body.style.background = th.bg; }
        else { root.style.removeProperty('--qr-bg'); document.body.style.background = ''; }
        return () => { root.style.removeProperty('--qr-primary'); root.style.removeProperty('--qr-bg'); document.body.style.background = ''; };
    }, [tenant, season]);
    const [activeCategory, setActiveCategory] = useState(null);
    const [search, setSearch] = useState('');
    const [cart, setCart] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [zoomSrc, setZoomSrc] = useState(null); // şəkil böyütmə (lightbox)
    const [cartOpen, setCartOpen] = useState(false);
    const [variantModal, setVariantModal] = useState({ open: false, item: null });
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [customerNote, setCustomerNote] = useState('');
    const [submitError, setSubmitError] = useState('');

    const sectionRefs = useRef({});
    const rightScrollRef = useRef(null);
    const leftButtonRefs = useRef({});
    const manualScrollRef = useRef(false);

    useEffect(() => {
        let active = true;
        (async () => {
            if (!token) { setError('QR menyu linki yanlışdır.'); setLoading(false); return; }
            try {
                const res = await api.get(`/menu/public/full?token=${encodeURIComponent(token)}`);
                if (!active) return;
                setTenant(res.data?.tenant || null);
                setOccupancy(res.data?.occupancy || null);
                const cats = res.data?.data || [];
                setCategories(cats);
                if (cats.length) setActiveCategory(String(cats[0].id));
            } catch (err) {
                if (active) setError(err.response?.data?.msg || 'Menyu yüklənmədi.');
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => { active = false; };
    }, [token]);

    const visibleCategories = useMemo(() => {
        const q = normalizeText(search);
        if (!q) return categories.map((c) => ({ ...c, items: sortByPrice(c.items) }));
        return categories
            .map((c) => ({ ...c, items: sortByPrice((c.items || []).filter((it) => normalizeText(it.name).includes(q))) }))
            .filter((c) => c.items.length > 0);
    }, [search, categories]);

    const cartCount = useMemo(() => Object.values(cart).reduce((s, e) => s + e.qty, 0), [cart]);
    const cartTotal = useMemo(() => Object.values(cart).reduce((s, e) => s + e.qty * e.unit_price, 0), [cart]);

    const addItem = (item, variant = null) => {
        const variantId = variant?.id ?? null;
        const key = makeCartKey(item.id, variantId);
        const portion = portionOf(item);
        const unitPrice = variant
            ? variantDisplayPrice(item, variant)
            : (portion ? portion.price : Number(item.price));
        const suffix = variant
            ? ` (${variant.name}${portion ? `, ${portion.label}` : ''})`
            : (portion ? ` (${portion.label})` : '');
        setCart((p) => ({
            ...p,
            [key]: {
                item_id: item.id,
                variant_id: variantId,
                name: item.name + suffix,
                qty: (p[key]?.qty || 0) + 1,
                unit_price: unitPrice,
                image_url: item.image_url || null
            }
        }));
    };

    const removeOne = (key) => {
        setCart((p) => {
            const cur = p[key]; if (!cur) return p;
            const next = { ...p };
            if (cur.qty <= 1) delete next[key];
            else next[key] = { ...cur, qty: cur.qty - 1 };
            return next;
        });
    };

    const handleAdd = (item) => {
        // Vaxtı bitmiş set (QR-da qırmızı göstərilir) sifariş oluna bilməz
        if (item.is_set && item.available_now === false) return;
        const variants = item.variants || [];
        if (variants.length > 0) {
            setVariantModal({ open: true, item });
        } else {
            addItem(item);
        }
    };

    const scrollToCategory = (id) => {
        const sid = String(id);
        manualScrollRef.current = true; // scroll-spy observer-i sus
        setActiveCategory(sid);
        // Render + layout-dan SONRA scroll et (ikiqat rAF) — yoxsa bəzən tutmurdu.
        // Instant (smooth deyil) ki, məzmun dəyişəndə yarıda qalmasın.
        requestAnimationFrame(() => requestAnimationFrame(() => {
            const container = rightScrollRef.current;
            const hasSearch = String(search || '').trim().length > 0;
            if (hasSearch) {
                const el = sectionRefs.current[sid];
                if (el?.scrollIntoView) el.scrollIntoView({ block: 'start' });
                else if (container) container.scrollTop = 0;
            } else if (container) {
                container.scrollTop = 0;
            }
            setTimeout(() => { manualScrollRef.current = false; }, 250);
        }));
    };

    // Sağ paneldə scroll etdikcə aktiv kateqoriyanı avtomatik dəyiş
    useEffect(() => {
        const container = rightScrollRef.current;
        if (!container || visibleCategories.length === 0) return;
        const observer = new IntersectionObserver(
            (entries) => {
                if (manualScrollRef.current) return;
                const inView = entries
                    .filter((e) => e.isIntersecting)
                    .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
                if (inView.length > 0) {
                    const id = inView[0].target.getAttribute('data-cat-id');
                    if (id) setActiveCategory(id);
                }
            },
            {
                root: container,
                // Yuxarıdan 1px-də keçən başlıq aktiv say; aşağıdan 70% ignore
                rootMargin: '0px 0px -70% 0px',
                threshold: 0
            }
        );
        Object.values(sectionRefs.current).forEach((el) => el && observer.observe(el));
        return () => observer.disconnect();
    }, [visibleCategories]);

    // Aktiv kateqoriya dəyişəndə sol siyahıda görünür et
    useEffect(() => {
        if (!activeCategory) return;
        const btn = leftButtonRefs.current[String(activeCategory)];
        if (btn && btn.scrollIntoView) {
            btn.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
    }, [activeCategory]);

    const submitOrder = async () => {
        if (!cartCount) return;
        setSubmitting(true); setSubmitError('');
        try {
            const items = Object.values(cart).map((e) => ({
                item_id: e.item_id,
                variant_id: e.variant_id,
                qty: e.qty,
                note: ''
            }));
            await api.post('/qr-orders/submit', { token, items, note: customerNote });
            setSubmitted(true);
            setCart({});
        } catch (err) {
            setSubmitError(err.response?.data?.msg || 'Sifariş göndərilmədi.');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return <div className="flex h-screen items-center justify-center bg-gray-50 text-gray-400">Yüklənir...</div>;
    }
    if (error) {
        return (
            <div className="flex h-screen flex-col items-center justify-center bg-gray-50 text-center px-6">
                <div className="text-5xl mb-3">⚠️</div>
                <p className="text-gray-700 font-semibold">{error}</p>
            </div>
        );
    }
    if (submitted) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 text-center">
                <div className="max-w-sm rounded-3xl bg-white p-8 shadow-sm">
                    <div className="mx-auto mb-5 flex h-20 w-20 items-center justify-center rounded-full text-4xl text-white" style={{ backgroundColor: BRAND }}>✓</div>
                    <h2 className="mb-2 text-xl font-black text-gray-800">Sifarişiniz qəbul olundu</h2>
                    <p className="text-sm text-gray-500">Tezliklə ofisiant masanıza yaxınlaşacaq.</p>
                </div>
            </div>
        );
    }

    const orderingEnabled = Number(tenant?.qr_menu_ordering_enabled ?? 1);

    return (
        <div className="flex h-screen flex-col bg-[#f4f8fa]" style={{ touchAction: 'manipulation', background: 'var(--qr-bg, #f4f8fa)' }}>
            {/* Header */}
            <header className="flex items-center justify-between gap-3 border-b border-gray-200 bg-white px-4 py-3 shrink-0">
                <div className="flex min-w-0 items-center gap-2">
                    {onHome && (
                        <button
                            type="button"
                            onClick={onHome}
                            title="Əsas səhifəyə qayıt"
                            className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-base text-[#006395]"
                        >
                            🏠
                        </button>
                    )}
                    <div className="min-w-0">
                        <div className="text-[10px] font-bold uppercase tracking-[0.24em]" style={{ color: BRAND }}>QR menyu</div>
                        <h1 className="text-base font-black text-gray-900 truncate">{tenant?.name || 'Menyu'}</h1>
                        {occupancy && occupancy.total > 0 && (
                            <div className={`mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${
                                occupancy.percent >= 70 ? 'bg-rose-100 text-rose-700'
                                : occupancy.percent >= 40 ? 'bg-amber-100 text-amber-700'
                                : 'bg-emerald-100 text-emerald-700'
                            }`}>
                                🍽️ {occupancy.percent}% doludur
                            </div>
                        )}
                    </div>
                </div>
                {orderingEnabled > 0 && (
                    <button
                        onClick={() => setCartOpen(true)}
                        className="relative flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold text-white shadow-sm"
                        style={{ background: `linear-gradient(135deg, ${BRAND}, var(--qr-primary-2, #006f91))` }}
                    >
                        🛒
                        <span>{formatMoney(cartTotal)}</span>
                        {cartCount > 0 && (
                            <span className="absolute -top-1 -right-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-black text-white">
                                {cartCount}
                            </span>
                        )}
                    </button>
                )}
            </header>

            {/* Search */}
            <div className="border-b border-gray-100 bg-white px-4 py-2.5 shrink-0">
                <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">🔍</span>
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Məhsul axtar..."
                        className="w-full rounded-xl border border-gray-200 bg-gray-50 pl-9 pr-3 py-2 text-sm focus:border-primary-400 focus:outline-none"
                    />
                </div>
            </div>

            {/* Body: left categories + right items */}
            <div className="flex flex-1 min-h-0 overflow-hidden">
                {/* SOL — kateqoriyalar şaquli */}
                <aside
                    className="w-24 sm:w-32 md:w-40 lg:w-48 shrink-0 overflow-y-auto"
                    style={{ background: `linear-gradient(180deg, ${BRAND} 0%, var(--qr-primary-2, #006f91) 100%)` }}
                >
                    <div className="py-2">
                        {visibleCategories.map((cat) => {
                            const isActive = String(activeCategory) === String(cat.id);
                            return (
                                <button
                                    key={cat.id}
                                    ref={(el) => { leftButtonRefs.current[String(cat.id)] = el; }}
                                    type="button"
                                    onClick={() => scrollToCategory(cat.id)}
                                    className={`relative w-full px-2 py-3 text-left transition-all ${
                                        isActive ? 'bg-white/15' : 'hover:bg-white/10'
                                    }`}
                                    style={{ borderLeft: isActive ? '4px solid #ffffff' : '4px solid transparent' }}
                                >
                                    {cat.image_url ? (
                                        <img
                                            src={toAssetUrl(cat.image_url)}
                                            alt={cat.name}
                                            className="mx-auto mb-1.5 h-12 w-12 rounded-xl object-cover shadow-sm ring-2 ring-white/40 sm:h-14 sm:w-14"
                                            onError={(e) => { e.target.style.display = 'none'; }}
                                        />
                                    ) : (
                                        <div
                                            className="mx-auto mb-1.5 flex h-12 w-12 items-center justify-center rounded-xl text-lg font-black sm:h-14 sm:w-14"
                                            style={{
                                                backgroundColor: isActive ? '#ffffff' : 'rgba(255,255,255,0.18)',
                                                color: isActive ? BRAND : '#ffffff'
                                            }}
                                        >
                                            {String(cat.name || '?').charAt(0).toUpperCase()}
                                        </div>
                                    )}
                                    <div className={`text-center text-[11px] font-bold leading-tight sm:text-xs ${isActive ? 'text-white' : 'text-white/85'}`}>
                                        {cat.name}
                                    </div>
                                    <div className={`mt-0.5 text-center text-[9px] ${isActive ? 'text-white/90' : 'text-white/60'}`}>
                                        {(cat.items || []).length}
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </aside>

                {/* SAG — məhsul siyahısı.
                    Axtarış varsa bütün tapılan kateqoriyalar.
                    Axtarış yoxdursa yalnız SEÇİLMİŞ kateqoriya göstərilir. */}
                <main ref={rightScrollRef} className="flex-1 overflow-y-auto px-3 py-3 sm:px-5">
                    {/* Mövsümi təbrik (V1-dəki kimi) */}
                    <SeasonBanner season={season} />
                    {(() => {
                        const hasSearch = String(search || '').trim().length > 0;
                        const shown = hasSearch
                            ? visibleCategories
                            : visibleCategories.filter(c => String(c.id) === String(activeCategory));
                        if (shown.length === 0) {
                            return (
                                <div className="flex h-64 flex-col items-center justify-center text-gray-400 gap-2">
                                    <div className="text-4xl">🍽️</div>
                                    <p className="text-sm">Heç nə tapılmadı</p>
                                </div>
                            );
                        }
                        return shown.map((cat) => (
                            <section
                                key={cat.id}
                                ref={(el) => { sectionRefs.current[String(cat.id)] = el; }}
                                data-cat-id={String(cat.id)}
                                className="mb-6 scroll-mt-2"
                            >
                                <h2 className="qr-on-bg mb-3 flex items-center gap-2 text-lg font-black text-gray-900">
                                    <span className="h-6 w-1 rounded-full" style={{ backgroundColor: BRAND }} />
                                    <span>{cat.name}</span>
                                </h2>
                                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                    {(cat.items || []).map((item) => {
                                        const hasVariants = (item.variants || []).length > 0;
                                        const inCart = Object.values(cart).filter((c) => c.item_id === item.id).reduce((s, e) => s + e.qty, 0);
                                        return (
                                            <article
                                                key={item.id}
                                                className="group flex overflow-hidden rounded-2xl bg-white shadow-sm transition-all hover:shadow-md"
                                            >
                                                <div className="h-28 w-28 flex-shrink-0 overflow-hidden bg-[#edf6fa]">
                                                    {item.image_url ? (
                                                        <WatermarkedImage
                                                            src={toAssetUrl(item.image_url)}
                                                            alt={item.name}
                                                            onClick={(e) => { e.stopPropagation(); setZoomSrc(toAssetUrl(item.image_url)); }}
                                                            imgClassName="h-full w-full object-cover transition-transform group-hover:scale-105"
                                                            fontSize={14}
                                                        />
                                                    ) : (
                                                        <div className="flex h-full w-full items-center justify-center text-3xl" style={{ color: BRAND, opacity: 0.4 }}>
                                                            🍽️
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex flex-1 flex-col justify-between p-3 min-w-0">
                                                    <div className="min-w-0">
                                                        <h3 className="font-bold text-gray-900 text-sm leading-tight line-clamp-2">{item.name}</h3>
                                                        {Number(item.portion_pieces) > 1 && (
                                                            <div className="mt-0.5 inline-block rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-700">
                                                                1 porsiya = {item.portion_pieces} ədəd
                                                            </div>
                                                        )}
                                                        {item.description && (
                                                            item.is_set ? (
                                                                <ul className="mt-1 space-y-0.5 rounded-lg bg-gray-50 px-2 py-1 text-[11px] leading-4 text-gray-600">
                                                                    {item.description.split(',').map((part, idx) => {
                                                                        const t = part.trim();
                                                                        return t ? <li key={idx} className="flex gap-1"><span className="text-gray-400">•</span><span>{t}</span></li> : null;
                                                                    })}
                                                                </ul>
                                                            ) : (
                                                                <p className="mt-1 text-[11px] text-gray-400 line-clamp-2">{item.description}</p>
                                                            )
                                                        )}
                                                        {item.is_set && item.schedule_text && (
                                                            <p className={`mt-1 text-[11px] font-bold ${item.available_now ? 'text-emerald-600' : 'text-red-500'}`}>
                                                                {item.available_now ? `🟢 Aktivdir · ${item.schedule_text}` : `🔴 ${item.schedule_text}`}
                                                            </p>
                                                        )}
                                                        {/* Variant qiymətləri kartda görünsün (sifariş bağlı olsa belə) */}
                                                        {hasVariants && (
                                                            <div className="mt-1 flex flex-wrap gap-x-2 gap-y-0.5 text-[11px] leading-4 text-gray-500">
                                                                {(item.variants || []).map((v) => (
                                                                    <span key={v.id}>
                                                                        <span className="font-semibold text-gray-600">{v.name}:</span>{' '}
                                                                        <span className="font-bold" style={{ color: BRAND }}>{formatMoney(variantDisplayPrice(item, v))}</span>
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="mt-2 flex items-center justify-between gap-2">
                                                        <div className="font-black text-base" style={{ color: BRAND }}>
                                                            {hasVariants
                                                                ? (portionOf(item)
                                                                    ? `${portionOf(item).label} · ${(item.variants || []).length} seçim`
                                                                    : `${(item.variants || []).length} seçim`)
                                                                : (portionOf(item)
                                                                    ? `${portionOf(item).label} — ${formatMoney(portionOf(item).price)}`
                                                                    : formatMoney(item.price))}
                                                        </div>
                                                        {orderingEnabled > 0 && (
                                                            item.is_set && item.available_now === false ? (
                                                                <span className="rounded-full bg-gray-100 px-2.5 py-1 text-[10px] font-bold text-gray-400">Vaxtı deyil</span>
                                                            ) : (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleAdd(item)}
                                                                    className="flex h-9 w-9 items-center justify-center rounded-full text-lg font-bold text-white shadow-sm transition-all hover:scale-110 active:scale-95"
                                                                    style={{ background: `linear-gradient(135deg, ${BRAND}, var(--qr-primary-2, #006f91))` }}
                                                                >
                                                                    +
                                                                </button>
                                                            )
                                                        )}
                                                    </div>
                                                    {inCart > 0 && (
                                                        <div className="mt-1 text-[10px] font-bold" style={{ color: BRAND }}>
                                                            Səbətdə: {inCart}
                                                        </div>
                                                    )}
                                                </div>
                                            </article>
                                        );
                                    })}
                                </div>
                            </section>
                        ));
                    })()}
                    <div className="h-20" />
                </main>
            </div>

            {/* Sticky cart bar */}
            {orderingEnabled > 0 && cartCount > 0 && !cartOpen && (
                <button
                    type="button"
                    onClick={() => setCartOpen(true)}
                    className="fixed bottom-4 left-1/2 z-40 flex w-[calc(100%-2rem)] max-w-sm -translate-x-1/2 items-center justify-between gap-2 whitespace-nowrap rounded-full px-4 py-3 text-sm font-bold text-white shadow-2xl"
                    style={{ background: `linear-gradient(135deg, ${BRAND}, var(--qr-primary-2, #006f91))` }}
                >
                    <span className="flex items-center gap-2">
                        <span className="flex h-6 min-w-[24px] items-center justify-center rounded-full bg-white/25 px-1.5 text-xs">{cartCount}</span>
                        <span>Səbətə bax</span>
                    </span>
                    <span className="whitespace-nowrap rounded-full bg-white/25 px-3 py-1 tabular-nums">{formatMoney(cartTotal)}</span>
                </button>
            )}

            {/* Cart drawer */}
            {cartOpen && (
                <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4"
                    onClick={(e) => e.target === e.currentTarget && setCartOpen(false)}>
                    <div className="flex max-h-[88vh] w-full sm:max-w-md flex-col rounded-t-3xl sm:rounded-2xl bg-white shadow-xl">
                        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4 shrink-0">
                            <h2 className="text-base font-black text-gray-900">🛒 Səbət ({cartCount})</h2>
                            <button onClick={() => setCartOpen(false)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
                        </div>
                        <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-3">
                            {cartCount === 0 ? (
                                <p className="text-center text-sm text-gray-400 py-10">Səbət boşdur</p>
                            ) : (
                                Object.entries(cart).map(([key, e]) => (
                                    <div key={key} className="flex items-center gap-3 rounded-xl border border-gray-100 p-2.5">
                                        <div className="h-12 w-12 flex-shrink-0 overflow-hidden rounded-lg bg-gray-100">
                                            {e.image_url ? (
                                                <img src={toAssetUrl(e.image_url)} alt="" className="h-full w-full object-cover" />
                                            ) : (
                                                <div className="flex h-full w-full items-center justify-center text-xl" style={{ color: BRAND, opacity: 0.4 }}>🍽️</div>
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-bold text-gray-800 truncate">{e.name}</div>
                                            <div className="text-xs text-gray-400">{formatMoney(e.unit_price)} × {e.qty}</div>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            <button onClick={() => removeOne(key)} className="h-7 w-7 rounded-full border border-gray-200 text-sm font-bold text-gray-500 hover:bg-gray-50">−</button>
                                            <span className="min-w-[20px] text-center font-bold">{e.qty}</span>
                                            <button
                                                onClick={() => setCart((p) => ({ ...p, [key]: { ...p[key], qty: p[key].qty + 1 } }))}
                                                className="h-7 w-7 rounded-full text-sm font-bold text-white"
                                                style={{ backgroundColor: BRAND }}
                                            >+</button>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                        {cartCount > 0 && (
                            <div className="border-t border-gray-100 px-5 py-4 shrink-0 space-y-3">
                                <textarea
                                    value={customerNote}
                                    onChange={(e) => setCustomerNote(e.target.value)}
                                    placeholder="Qeyd (məsələn: masa nömrəsi, alleryalar...)"
                                    rows={2}
                                    className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-primary-400 focus:outline-none resize-none"
                                />
                                {submitError && <p className="text-xs text-red-500 font-semibold">{submitError}</p>}
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-gray-500">Cəmi:</span>
                                    <span className="text-lg font-black" style={{ color: BRAND }}>{formatMoney(cartTotal)}</span>
                                </div>
                                <button
                                    onClick={submitOrder}
                                    disabled={submitting}
                                    className="w-full rounded-2xl py-3 text-sm font-black uppercase tracking-wider text-white shadow-md disabled:opacity-50"
                                    style={{ background: `linear-gradient(135deg, ${BRAND}, var(--qr-primary-2, #006f91))` }}
                                >
                                    {submitting ? 'Göndərilir...' : 'Sifarişi göndər'}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Variant modal */}
            {variantModal.open && variantModal.item && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
                    onClick={(e) => e.target === e.currentTarget && setVariantModal({ open: false, item: null })}>
                    <div className="w-full max-w-sm rounded-2xl bg-white shadow-xl">
                        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
                            <h2 className="text-base font-black text-gray-900 truncate">{variantModal.item.name}</h2>
                            <button onClick={() => setVariantModal({ open: false, item: null })} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
                        </div>
                        <div className="px-5 py-4 space-y-2">
                            <p className="text-xs text-gray-400 mb-1">
                                Variant seçin{portionOf(variantModal.item) ? ` (${portionOf(variantModal.item).label})` : ''}:
                            </p>
                            {(variantModal.item.variants || []).map((v) => (
                                <button
                                    key={v.id}
                                    type="button"
                                    onClick={() => { addItem(variantModal.item, v); setVariantModal({ open: false, item: null }); }}
                                    className="flex w-full items-center justify-between rounded-xl border border-gray-200 px-4 py-3 text-left transition-all hover:border-primary-400 hover:bg-primary-50/30"
                                >
                                    <span className="font-bold text-sm text-gray-800">{v.name}</span>
                                    <span className="font-black" style={{ color: BRAND }}>{formatMoney(variantDisplayPrice(variantModal.item, v))}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}
            <ImageLightbox src={zoomSrc} onClose={() => setZoomSrc(null)} />
        </div>
    );
};

export default QrMenuV2;
